import React, { useEffect, useRef } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { FormField } from "components/form/FormField/FormField";
import { TemplateVariablePicker } from "components/TemplateVariablePicker/TemplateVariablePicker";
import { Button } from "components/ui/Button";
import { Dialog, DialogFooter } from "components/ui/Dialog";
import { onInvalidSubmit } from "utils/reportFormErrors";

import styles from "../../../shared/templateModal.module.css";

const smsTemplateSchema = z.object({
  name: z.string().min(1, "Please enter name."),
  template: z.string().min(1, "Please enter template text."),
});

const DEFAULT_VALUES = {
  name: "",
  template: "",
};

export const SmsTemplateModal = ({ isOpen, onClose, onSubmit, editTemplate }) => {
  const methods = useForm({
    resolver: zodResolver(smsTemplateSchema),
    defaultValues: DEFAULT_VALUES,
  });

  const { control, handleSubmit, reset, getValues, setValue } = methods;
  const textareaRef = useRef(null);

  const handleInsertVariable = (placeholder) => {
    const textarea = textareaRef.current;

    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const current = getValues("template") || "";
      const newValue =
        current.substring(0, start) + placeholder + current.substring(end);
      setValue("template", newValue, { shouldDirty: true });

      requestAnimationFrame(() => {
        const cursorPosition = start + placeholder.length;
        textarea.setSelectionRange(cursorPosition, cursorPosition);
        textarea.focus();
      });
    } else {
      const current = getValues("template") || "";
      setValue("template", current + placeholder, { shouldDirty: true });
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    reset(
      editTemplate
        ? { name: editTemplate.name, template: editTemplate.template }
        : DEFAULT_VALUES
    );
  }, [isOpen, editTemplate, reset]);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      title={editTemplate ? "Edit Template" : "Add New Template"}
      width={720}
    >
      <FormProvider {...methods}>
        <form className={styles.form} onSubmit={handleSubmit(onSubmit, onInvalidSubmit("SmsTemplateModal"))}>
          <div className={styles.fields}>
            <div className={styles.fieldGroup}>
              <label className={styles.label}>Name</label>
              <FormField name="name">
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="text"
                      placeholder="Name"
                      className={styles.input}
                    />
                  )}
                />
              </FormField>
            </div>

            <div className={styles.fieldGroup}>
              <div className={styles.labelRow}>
                <label className={styles.label}>Template Text</label>
                <TemplateVariablePicker onInsert={handleInsertVariable} />
              </div>
              <FormField name="template">
                <Controller
                  name="template"
                  control={control}
                  render={({ field }) => (
                    <textarea
                      {...field}
                      ref={(element) => {
                        field.ref(element);
                        textareaRef.current = element;
                      }}
                      rows={7}
                      placeholder="Template"
                      className={styles.textarea}
                    />
                  )}
                />
              </FormField>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" size="medium" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" size="medium" type="submit">
              Save
            </Button>
          </DialogFooter>
        </form>
      </FormProvider>
    </Dialog>
  );
};
